import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Star, 
  Clock, 
  Command, 
  Bell, 
  User, 
  ChevronDown,
  History,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useNavigation, BreadcrumbNavigation, QuickSwitcher, BackButton } from './NavigationBridge';
import { FavoritesManager, FavoriteButton } from './FavoritesManager';
// Temporarily comment out session recovery import due to path resolution issue
// import { useSessionRecovery, SessionRecoveryModal } from '../../../hooks/useSessionRecovery';

interface NavigationHeaderProps {
  title: string;
  subtitle?: string;
  currentItem?: {
    type: 'template' | 'test' | 'agent' | 'campaign';
    name: string;
    path: string;
    context?: Record<string, any>;
  };
  actions?: React.ReactNode;
  showBreadcrumbs?: boolean;
  onCreateTest?: () => void;
  showCreateButton?: boolean;
}

export const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  title,
  subtitle,
  currentItem,
  actions,
  showBreadcrumbs = true,
  onCreateTest,
  showCreateButton = false
}) => {
  const { state, resetNavigation } = useNavigation();
  const [showQuickSwitcher, setShowQuickSwitcher] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Temporary mock state for session recovery (until import issue is resolved)
  const [showSessionRecovery, setShowSessionRecovery] = useState(false);
  const sessions: any[] = [];
  const currentSession = null;
  const hasUnsavedChanges = false;
  const getUnsavedSessions = () => [];

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowQuickSwitcher(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Removed unused handleSessionRestore function

  return (
    <>
      {/* Global App Header - Top Level */}
      <div className="bg-gray-50 dark:bg-dark-700 border-b border-line sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-end py-2 space-x-3">
            {/* Recent items button */}
            <button
              onClick={() => {/* Show recent items */}}
              className="relative p-2 text-content-secondary hover:text-content hover: rounded-lg transition-colors"
              title="Recent items"
            >
              <Clock className="w-4 h-4" />
              {state.recentItems.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-xs">
                  {state.recentItems.length > 9 ? '9+' : state.recentItems.length}
                </span>
              )}
            </button>

            {/* Session recovery button */}
            {sessions.length > 0 && (
              <button
                onClick={() => setShowSessionRecovery(true)}
                className="relative p-2 text-content-secondary hover:text-content hover: rounded-lg transition-colors"
                title="Session recovery"
              >
                <History className="w-4 h-4" />
                {getUnsavedSessions().length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-xs">
                    {getUnsavedSessions().length}
                  </span>
                )}
              </button>
            )}

            {/* Notifications */}
            <button className="relative p-2 text-content-secondary hover:text-content hover: rounded-lg transition-colors">
              <Bell className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-xs">
                3
              </span>
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-1 p-2 text-content-secondary hover:text-content hover: rounded-lg transition-colors"
              >
                <User className="w-4 h-4" />
                <ChevronDown className="w-3 h-3" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-surface rounded-lg shadow-lg border border-line py-1 z-50">
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700">
                    Profile Settings
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700">
                    Preferences
                  </button>
                  <hr className="my-1" />
                  <button 
                    onClick={() => {
                      resetNavigation();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    🔧 Clear Navigation (Debug)
                  </button>
                  <hr className="my-1" />
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700">
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Feature Header - A/B Testing Suite */}
      <div className="bg-surface border-b border-line sticky top-8 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Left section */}
            <div className="flex items-center space-x-4 min-w-0 flex-1">
              <BackButton />
              
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-3">
                  <h1 className="text-xl font-semibold text-content truncate">{title}</h1>
                  {currentItem && (
                    <FavoriteButton 
                      item={currentItem}
                      className="flex-shrink-0"
                    />
                  )}
                  {hasUnsavedChanges && (
                    <div className="flex items-center space-x-1 text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded-full text-xs">
                      <AlertCircle className="w-3 h-3" />
                      <span>Unsaved</span>
                    </div>
                  )}
                </div>
                {subtitle && (
                  <p className="text-sm text-content-secondary mt-1 truncate">{subtitle}</p>
                )}
              </div>
            </div>

            {/* Right section */}
            <div className="flex items-center space-x-3">
              {/* Create New Test Button */}
              {showCreateButton && onCreateTest && (
                <button
                  onClick={onCreateTest}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Test
                </button>
              )}

              {/* Custom actions */}
              {actions}

              {/* Quick switcher button */}
              <button
                onClick={() => setShowQuickSwitcher(true)}
                className="flex items-center space-x-2 px-3 py-2 text-content-secondary hover:text-content hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                title="Quick switcher (⌘K)"
              >
                <Search className="w-4 h-4" />
                <div className="hidden sm:flex items-center space-x-1 text-xs text-content-subtle">
                  <Command className="w-3 h-3" />
                  <span>K</span>
                </div>
              </button>

              {/* Favorites button */}
              <button
                onClick={() => setShowFavorites(true)}
                className="relative p-2 text-content-secondary hover:text-content hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                title="Favorites & Bookmarks"
              >
                <Star className="w-5 h-5" />
                {state.favorites.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {state.favorites.length > 9 ? '9+' : state.favorites.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Breadcrumbs */}
          {showBreadcrumbs && (
            <div className="pb-4">
              <BreadcrumbNavigation />
            </div>
          )}
        </div>

        {/* Auto-save indicator */}
        {currentSession && (
          <div className="absolute top-full right-4 mt-2 z-30">
            <div className="bg-surface border border-line rounded-lg shadow-sm px-3 py-2 flex items-center space-x-2 text-xs text-content-secondary">
              {hasUnsavedChanges ? (
                <>
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <span>Auto-saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-3 h-3 text-green-500 dark:text-green-400" />
                  <span>All changes saved</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <QuickSwitcher 
        isOpen={showQuickSwitcher} 
        onClose={() => setShowQuickSwitcher(false)} 
      />
      
      <FavoritesManager 
        isOpen={showFavorites} 
        onClose={() => setShowFavorites(false)} 
      />

      {/* SessionRecoveryModal temporarily disabled due to import issue */}
      {showSessionRecovery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">Session Recovery</h3>
            <p className="text-content-secondary mb-4">Session recovery feature will be available soon.</p>
            <button
              onClick={() => setShowSessionRecovery(false)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Click outside handler for user menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </>
  );
};